<template>
  <div>
    <v-card class="ma-2">
      <v-card-text>  
        <v-row>
          <v-col>
            <v-autocomplete
              label="Select a conformation entry to edit (date • time • show type)"
              clearable
              v-model="selectedCombination"
              :item-value="(item) => item"
              :item-text="(item) => `${$dateUtils.formatDate(item.showDate)}  •  ${item.showTime}  •  ${item.showType}`"
              :items="enteredCombinations"
              :disabled="!enteredCombinations || !enteredCombinations.length" />
          </v-col>
          <v-col class="d-flex justify-end">
            <v-btn v-if="!!selectedCombination"
              color="secondary"
              class="mr-2"
              v-on:click="deleteEntry">
              Delete
            </v-btn>
          </v-col>
        </v-row>
        <v-divider />
        <v-row>
          <v-col>
            <v-radio-group mandatory row v-model="editedInfo.showType">
              <v-radio
                color="primary" 
                label="Intact"
                value="I"
              />
              <v-radio
                color="primary" 
                label="Altered"
                value="A"
              />
            </v-radio-group>
          </v-col>
          <v-col>
            <DatepickerFormatted
                label="Show Date"
                :disableClearable="true"
                :date="editedInfo.showDate"
                v-on:updateDates="($event) => updateDates($event, 'showDate')" />
          </v-col>
          <v-col>
            <v-text-field
                label="Show Time"
                v-model="editedInfo.showTime"
                :rules="[(v) => !!v || 'Required', v => (!!v && (v === 'AM' || v === 'PM')) || 'AM or PM only']" /> 
          </v-col>
        </v-row>
        <v-divider />
        <v-row class="mt-2">
          <v-col>
            <AdminDogSearchDialog 
              label="WD (Winner Dog)"
              v-bind:dogId.sync="editedInfo.winnerDogId"
              :initialDogId="persistedInfo.winnerDogId" 
              :isEditMode="true" />
          </v-col>
          <v-col>
            <AdminDogSearchDialog 
              label="WB (Winner Bitch)"
              v-bind:dogId.sync="editedInfo.winnerBitchId"  
              :initialDogId="persistedInfo.winnerBitchId"
              :isEditMode="true" />
          </v-col>
          <v-col>
            <AdminDogSearchDialog 
              label="BOB (Best Of Breed)"
              v-bind:dogId.sync="editedInfo.bestOfBreedId" 
              :initialDogId="persistedInfo.bestOfBreedId" 
              :isEditMode="true" />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-row class="ma-0 pa-0">
              <v-col cols="8" class="ma-0 pa-0">
                <v-text-field
                  v-model="editedInfo.winnerDogPoints"
                  label="WD Points"
                  clearable
                  type="numeric"
                  :rules="[(v) => !isNaN(v) || 'Numeric value required']"
                  @click:clear="editedInfo.winnerDogPoints = null" />
              </v-col>
              <v-col class="ma-0 pa-0" />
            </v-row>
          </v-col>
          <v-col>
            <v-row class="ma-0 pa-0">
              <v-col cols="8" class="ma-0 pa-0">
                <v-text-field 
                  v-model="editedInfo.winnerBitchPoints"
                  label="WB Points"
                  clearable
                  type="numeric"
                  :rules="[(v) => !isNaN(v) || 'Numeric value required']"
                  @click:clear="editedInfo.winnerBitchPoints = null" />
              </v-col>
              <v-col class="ma-0 pa-0" />
            </v-row>
          </v-col> 
          <v-col>
            <AdminDogSearchDialog 
              label="BOS (Best Of Opposite Sex)"
              v-bind:dogId.sync="editedInfo.bestOfOppositeSexId"
              :initialDogId="persistedInfo.bestOfOppositeSexId" 
              :isEditMode="true" />
          </v-col>
        </v-row>
        <v-row class="mt-2">
          <v-col>
            <AdminDogSearchDialog 
              label="RWD (Reserve Winner Dog)"
              v-bind:dogId.sync="editedInfo.reserveWinnerDogId"
              :initialDogId="persistedInfo.reserveWinnerDogId" 
              :isEditMode="true" />
          </v-col>
          <v-col>
            <AdminDogSearchDialog 
              label="RWB (Reserve Winner Bitch)"
              v-bind:dogId.sync="editedInfo.reserveWinnerBitchId" 
              :initialDogId="persistedInfo.reserveWinnerBitchId" 
              :isEditMode="true" />
          </v-col>
          <v-col>
            <AdminDogSearchDialog 
              label="BOW (Best Of Winners)"
              v-bind:dogId.sync="editedInfo.bestOfWinnersId" 
              :initialDogId="persistedInfo.bestOfWinnersId" 
              :isEditMode="true" />  
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <AdminJudgeSearchDialog 
              label="Dog Judge"
              v-bind:judgeId.sync="editedInfo.dogJudgeId" 
              :initialJudgeId="persistedInfo.dogJudgeId"
              :isEditMode="true" />
          </v-col>
          <v-col>       
            <v-text-field 
              v-model="editedInfo.specialDogsCount"
              label="# SD (Special Dogs Count)"
              clearable
              type="numeric"
              :rules="[(v) => !isNaN(v) || 'Numeric value required']"
              @click:clear="editedInfo.specialDogsCount = null" />
          </v-col>
          <v-col>
            <v-text-field 
              v-model="editedInfo.classDogsCount"
              label="# CD (Class Dogs Count)"
              clearable
              type="numeric"
              :rules="[(v) => !isNaN(v) || 'Numeric value required']"
              @click:clear="editedInfo.classDogsCount = null" />
          </v-col>  
        </v-row>
        <v-row class="mb-2">
          <v-col>
            <AdminJudgeSearchDialog 
              label="Bitch Judge"
              v-bind:judgeId.sync="editedInfo.bitchJudgeId" 
              :initialJudgeId="persistedInfo.bitchJudgeId"
              :isEditMode="true" />
          </v-col>
          <v-col>       
            <v-text-field 
              v-model="editedInfo.specialBitchesCount"
              label="# SB (Special Bitches Count)"
              clearable
              type="numeric"
              :rules="[(v) => !isNaN(v) || 'Numeric value required']"
              @click:clear="editedInfo.specialBitchesCount = null" />
          </v-col>  
          <v-col>
            <v-text-field 
              v-model="editedInfo.classBitchesCount"
              label="# CB (Class Bitches Count)"
              clearable
              type="numeric"
              :rules="[(v) => !isNaN(v) || 'Numeric value required']"
              @click:clear="editedInfo.classBitchesCount = null" />
          </v-col>
        </v-row>        
        <v-row class="mt-2">
          <v-col>
            <AdminDogSearchDialog 
              label="SelD (Select Dog)"
              v-bind:dogId.sync="editedInfo.selectDogId"
              :initialDogId="persistedInfo.selectDogId" 
              :isEditMode="true" />
          </v-col>
          <v-col>
            <AdminDogSearchDialog 
              label="SelB (Select Bitch)"
              v-bind:dogId.sync="editedInfo.selectBitchId" 
              :initialDogId="persistedInfo.selectBitchId" 
              :isEditMode="true" />
          </v-col>
          <v-col>              
          </v-col>
        </v-row>
        <v-divider />
        <v-row class="mt-2 mb-2 ">
          <v-col>
            <AdminPersonSearchDialog 
              label="BJH (Best Junior Handler)"
              v-bind:personId.sync="editedInfo.bestJuniorHandlerPersonId" 
              :isEditMode="true" />
          </v-col>
          <v-col>
            <AdminPersonSearchDialog 
              label="RJH (Reserve Junior Handler)"
              v-bind:personId.sync="editedInfo.reserveJuniorHandlerPersonId" 
              :isEditMode="true" />
          </v-col>
          <v-spacer />
        </v-row>
        <v-divider />
      </v-card-text>
      <v-card-actions>
        <v-row>
          <v-col>
            <p v-if="!!calculatedPoints">
              <span><b>CH - Calculated scores: </b></span><br/>
              <span>WD: {{calculatedPoints?.winnerDogScore == null ? 'N/A' : calculatedPoints.winnerDogScore}}</span>,
              <span>WB: {{calculatedPoints?.winnerBitchScore == null ? 'N/A' : calculatedPoints.winnerBitchScore}}</span>,
              <span>BOB: {{calculatedPoints?.bestOfBreedScore == null ? 'N/A' : calculatedPoints.bestOfBreedScore}}</span>,
              <span>BOS: {{calculatedPoints?.bestOfOppositeSexScore == null ? 'N/A' : calculatedPoints.bestOfOppositeSexScore}}</span>
            </p>
          </v-col>
          <v-col>
            <p v-if="!!calculatedPoints">
              <span><b>SCCH - Calculated scores: </b></span><br/>
              <span>BOB: {{calculatedPoints?.scchBestOfBreedScore == null ? 'N/A' : calculatedPoints.scchBestOfBreedScore}}</span>,
              <span>BOS: {{calculatedPoints?.scchBestOfOppositeSexScore == null ? 'N/A' : calculatedPoints.scchBestOfOppositeSexScore}}</span>,
              <span>SelD: {{calculatedPoints?.selectDogScore == null ? 'N/A' : calculatedPoints.selectDogScore}}</span>,
              <span>SelB: {{calculatedPoints?.selectBitchScore == null ? 'N/A' : calculatedPoints.selectBitchScore}}</span>
            </p>
          </v-col>
          <v-col>
            <v-row>
              <v-spacer /> 
              <v-col>
                <v-btn class="mt-2" icon v-on:click="calculatePoints" color="primary">
                  <v-icon>mdi-calculator</v-icon>
                </v-btn> 
                <v-btn color="primary" v-on:click="updateEntry">
                  Save
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>
    <v-snackbar v-model="showInfoMessage" title color="green">
      {{ infoMessage }}

      <template v-slot:action="{ attrs }">
        <v-btn color="primary" text v-bind="attrs" @click="showInfoMessage = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
    <v-snackbar v-model="showErrorMessage" title color="red">
      {{ errorMessage }}

      <template v-slot:action="{ attrs }">
        <v-btn color="primary" text v-bind="attrs" @click="showErrorMessage = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import * as _ from "lodash";
import AdminDogSearchDialog from "../../AdminDogSearchDialog.vue";
import AdminJudgeSearchDialog from "../../AdminJudgeSearchDialog.vue";
import AdminPersonSearchDialog from "../../AdminPersonSearchDialog.vue";
import DatepickerFormatted from "../../../common/inputs/DatepickerFormatted.vue";

import showService from "@/services/admin/showService";

import { Messages } from "@/constants";

export default Vue.extend({
  name: "ConformationShowResultsSummaryEntry",
  components: {
    AdminDogSearchDialog,
    AdminJudgeSearchDialog,
    AdminPersonSearchDialog,
    DatepickerFormatted,
 },
  data: function() {
    return {
      selectedCombination: null as any,
      enteredCombinations: [] as any[],
      existingCombinationSelected: false,
      editedInfo: { showDate: null } as any,
      persistedInfo: { showDate: null } as any,
      errorMessage: '',
      infoMessage: '',
      showErrorMessage: false,
      showInfoMessage: false,
      calculatedPoints: null as any,
    };
  },
  methods: {
    updateDates: function (val: any, key: string) {
      this.$data.editedInfo[key] = val.date;
    },
    reloadData: function() {
      this.$store.dispatch("changeLoaderStatus",true);
      showService.getConformationShowResultsSummaryList(this.showId)
        .then((res) => { 
          this.enteredCombinations = res; 
          this.selectedCombination = null;
          this.editedInfo = this.emptyEditedInfo();
          this.persistedInfo = this.emptyEditedInfo();
          this.errorMessage = '';
          this.infoMessage = '';
          this.showInfoMessage = false;
          this.showErrorMessage = false;
        }
      ).finally(() => { this.$store.dispatch("changeLoaderStatus",false); });
    }, 
    reloadDetails: function(selectedCombination: any){
      this.$store.dispatch("changeLoaderStatus",true);
      showService.getConformationShowResultsSummary(this.showId, selectedCombination.showDate, selectedCombination.showTime, selectedCombination.showType)
        .then((res) => { 
          this.editedInfo = _.cloneDeep(res);
          this.persistedInfo = _.cloneDeep(res);
        }
      ).finally(() => { this.$store.dispatch("changeLoaderStatus",false); });  
    },
    updateEntry: function(){
      this.$store.dispatch("changeLoaderStatus",true);
      showService.updateConformationShowResultsSummary(this.showId, this.editedInfo)
        .then((res) => {
          if(res.success) {
            this.reloadDetails(this.selectedCombination);
            this.infoMessage = Messages.SaveSuccessful;
            this.showInfoMessage = true;
          } else {
            this.errorMessage = res.messages.join(', ');
            this.showErrorMessage = true;
          }
        }
      ).finally(() => { this.$store.dispatch("changeLoaderStatus",false); });  
    },
    deleteEntry: function(){
      this.$store.dispatch("changeLoaderStatus",true);
      showService.deleteConformationShowResultsSummary(this.showId, this.selectedCombination.showDate, this.selectedCombination.showTime, this.selectedCombination.showType)
        .then((res) => {
          if(res.success) {
            this.reloadData();
            this.infoMessage = "Entry successfully deleted."
            this.showInfoMessage = true;
          } else {
            this.errorMessage = res.messages.join(', ');
            this.showErrorMessage = true;
          }
        }
      ).finally(() => { this.$store.dispatch("changeLoaderStatus",false); });  
    },
    checkIfExistingCombinationSelected: function(showDate: any, showTime: any, showType: any) {
        if(!showDate || !showTime || !showType) return;

        const idx = this.enteredCombinations.findIndex((e: any) => 
               this.$dateUtils.formatDate(e.showDate) == this.$dateUtils.formatDate(showDate) 
            && e.showTime?.trim() == showTime?.trim()
            && e.showType == showType);

        if (idx >= 0) {
          this.existingCombinationSelected = true;
          if(!this.selectedCombination) {
            this.errorMessage = 'Existing combination of date, time and type of show selected. Select combination from the dropdown to edit that entry.'
            this.showErrorMessage = true;
          }
        } else {
          this.existingCombinationSelected = false;
          this.errorMessage = '';
          this.showErrorMessage = false;
        }
    },
    calculatePoints: function() {
      this.$store.dispatch("changeLoaderStatus",true);
      showService.calculatePointsForConformationShowResultsSummary(this.showId, this.editedInfo)
        .then((res: any) =>{ this.calculatedPoints = res;})
        .finally(() => { this.$store.dispatch("changeLoaderStatus",false); });  
    },
    emptyEditedInfo: function() {
      return {
        winnerDogId: null as any,
        winnerBitchId: null as any,
        bestOfBreedId: null as any,
        winnerDogPoints: 0,
        winnerBitchPoints: 0,
        bestOfOppositeSexId: null as any,
        reserveWinnerDogId: null as any,
        reserveWinnerBitchId: null as any,
        dogJudgeId: null as any,
        specialDogsCount: 0,
        classDogsCount: 0,
        bitchJudgeId: null as any,
        specialBitchesCount: 0, 
        classBitchesCount:0,
        showType: "I",
        showDate: null,
        showTime: null,
        showId: this.showId,
        bestJuniorHandlerPersonId: null as any,
        reserveJuniorHandlerPersonId: null as any,
      };
    }
  },    
  created: function() {
    this.reloadData();
  },
  watch: {
    "selectedCombination": function(newVal: String, oldVal: String) {
      if(!newVal){
        this.reloadData();
        this.$set(this, 'editedInfo', this.editedInfo);
      } else if(newVal && newVal != oldVal){
        this.reloadDetails(newVal);
        this.errorMessage = '';
      }
    },
    "editedInfo.showDate": function(newVal: any, oldVal: any){
      if (newVal) {
        this.checkIfExistingCombinationSelected(newVal, this.editedInfo.showTime, this.editedInfo.showType);
      }
    },
    "editedInfo.showTime": function(newVal: any, oldVal: any){        
      if (newVal) {
        this.checkIfExistingCombinationSelected(this.editedInfo.showDate, newVal, this.editedInfo.showType);
      }
    },
    "editedInfo.showType": function(newVal: any, oldVal: any){
      if (newVal) {
        this.checkIfExistingCombinationSelected(this.editedInfo.showDate, this.editedInfo.showTime, newVal);
      }
    }
  },
  props: {
    showId: { type: String },
  },
});
</script>